import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Trash2, Plus, FileText, Settings, BookOpen, AlertCircle, ChevronDown, ChevronUp, Copy, Link2, ExternalLink } from 'lucide-react';
import { ICON_MAP, ICON_OPTIONS, COLOR_OPTIONS, DOC_SECTION_TEMPLATE, BLOCK_TEMPLATES, toEmbeddableUrl } from './documentLibraryConstants';

// ─── Category Manager Dialog ──────────────────────────────────

export function CategoryManagerDialog({ open, onClose, categories, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', icon: 'FileText', color: 'slate' });

  const openNew = () => { setEditing('new'); setForm({ name: '', icon: 'FileText', color: 'slate' }); };
  const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, icon: c.icon, color: c.color }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      if (editing === 'new') {
        await api.post('/doc-library/categories', form);
        toast.success('Category created');
      } else {
        await api.put(`/doc-library/categories/${editing}`, form);
        toast.success('Category updated');
      }
      setEditing(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/doc-library/categories/${deleteId}`);
      toast.success('Deleted');
      setDeleteId(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setDeleteId(null); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setEditing(null); onClose(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Settings className="w-4 h-4 text-amber-500" />Manage Categories</DialogTitle>
          </DialogHeader>

          {editing ? (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-9 text-sm" data-testid="cat-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Icon</Label>
                  <Select value={form.icon} onValueChange={v => setForm(p => ({ ...p, icon: v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="cat-icon-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {ICON_OPTIONS.map(name => {
                        const Icon = ICON_MAP[name];
                        return <SelectItem key={name} value={name}><span className="flex items-center gap-2 text-xs"><Icon className="w-3.5 h-3.5" />{name}</span></SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Colour</Label>
                  <Select value={form.color} onValueChange={v => setForm(p => ({ ...p, color: v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="cat-color-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COLOR_OPTIONS.map(c => (
                        <SelectItem key={c.id} value={c.id}><span className="flex items-center gap-2 text-xs"><span className={`w-3 h-3 rounded-full ${c.dot}`} />{c.label}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => setEditing(null)}>Cancel</Button>
                <Button size="sm" className="text-xs h-9 bg-amber-600 hover:bg-amber-700" onClick={save} data-testid="save-category-btn">
                  {editing === 'new' ? 'Create Category' : 'Update Category'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 mt-2">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs text-slate-500">{categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}</p>
                <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={openNew} data-testid="add-category-btn"><Plus className="w-3 h-3 mr-1" />Add Category</Button>
              </div>
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
                {categories.map(c => {
                  const Icon = ICON_MAP[c.icon] || FileText;
                  return (
                    <div key={c.id} className="flex items-center gap-3 border rounded-md px-3 py-2 hover:bg-slate-50">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${c.iconBg} ${c.iconColor}`}><Icon className="w-4 h-4" /></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{c.name}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{c.id}</p>
                      </div>
                      <Badge variant="outline" className="text-[9px]">{c.color}</Badge>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`}><Settings className="w-3.5 h-3.5 text-slate-400" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteId(c.id)} data-testid={`delete-cat-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                    </div>
                  );
                })}
                {categories.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-6">No categories yet. Add one to get started.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this category?</AlertDialogTitle>
            <AlertDialogDescription>The category must not be in use by any document. If it is, you'll see an error.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-xs" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


// ─── Document Editor Dialog ───────────────────────────────────

const defaultMeta = {
  title: '', summary: '', category: '', audience: '', author: '', version: '1.0',
  readMinutes: 3, updatedAt: '', tags: '',
  doc_type: 'authored', external_url: '',
};

export function DocumentEditorDialog({ open, mode, docId, categories, onClose, onSaved }) {
  const [meta, setMeta] = useState(defaultMeta);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && docId) {
      setLoading(true);
      api.get(`/doc-library/documents/${docId}`).then(r => {
        const d = r.data;
        setMeta({
          title: d.title, summary: d.summary, category: d.category || '', audience: (d.audience || []).join(', '),
          author: d.author, version: d.version, readMinutes: d.readMinutes, updatedAt: d.updatedAt,
          tags: (d.tags || []).join(', '),
          doc_type: d.doc_type || 'authored',
          external_url: d.external_url || '',
        });
        setSections(d.sections || []);
      }).finally(() => setLoading(false));
    } else {
      setMeta({ ...defaultMeta, category: categories[0]?.id || '' });
      setSections(JSON.parse(JSON.stringify(DOC_SECTION_TEMPLATE)));
    }
  }, [open, mode, docId, categories]);

  const save = async () => {
    if (!meta.title.trim()) { toast.error('Title required'); return; }
    if (!meta.category) { toast.error('Category required'); return; }
    if (meta.doc_type === 'external_link') {
      if (!meta.external_url.trim()) { toast.error('External URL is required'); return; }
      if (!/^https?:\/\//.test(meta.external_url.trim())) { toast.error('URL must start with http:// or https://'); return; }
    }
    setSaving(true);
    try {
      const payload = { ...meta, sections: meta.doc_type === 'external_link' ? [] : sections };
      if (mode === 'edit') {
        await api.put(`/doc-library/documents/${docId}`, payload);
        toast.success('Document updated');
      } else {
        await api.post('/doc-library/documents', payload);
        toast.success('Document created');
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  // Section helpers
  const addSection = () => setSections(p => [...p, { heading: 'New section', subheading: '', blocks: [] }]);
  const deleteSection = (i) => setSections(p => p.filter((_, idx) => idx !== i));
  const updateSection = (i, field, val) => setSections(p => p.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  const moveSection = (i, dir) => setSections(p => {
    const ni = i + dir;
    if (ni < 0 || ni >= p.length) return p;
    const a = [...p]; [a[i], a[ni]] = [a[ni], a[i]]; return a;
  });

  // Block helpers
  const addBlock = (si, type) => setSections(p => p.map((s, idx) => idx === si ? { ...s, blocks: [...s.blocks, JSON.parse(JSON.stringify(BLOCK_TEMPLATES[type]))] } : s));
  const deleteBlock = (si, bi) => setSections(p => p.map((s, idx) => idx === si ? { ...s, blocks: s.blocks.filter((_, j) => j !== bi) } : s));
  const updateBlock = (si, bi, updater) => setSections(p => p.map((s, idx) => idx === si ? { ...s, blocks: s.blocks.map((b, j) => j === bi ? updater(b) : b) } : s));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-violet-500" />
            {mode === 'edit' ? 'Edit Document' : 'Create Document'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-xs text-slate-400">Loading...</div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-6 pr-1">
            {/* Doc type toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setMeta(p => ({ ...p, doc_type: 'authored' }))}
                className={`flex-1 p-3 rounded-md border-2 text-left transition-all ${meta.doc_type === 'authored' ? 'border-violet-500 bg-violet-50/50' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid="doc-type-authored"
              >
                <div className="flex items-center gap-2 mb-1">
                  <FileText className={`w-4 h-4 ${meta.doc_type === 'authored' ? 'text-violet-600' : 'text-slate-400'}`} />
                  <p className={`text-sm font-semibold ${meta.doc_type === 'authored' ? 'text-violet-700' : 'text-slate-700'}`}>Authored Document</p>
                </div>
                <p className="text-[10px] text-slate-500">Write rich step-by-step content using sections & blocks.</p>
              </button>
              <button
                onClick={() => setMeta(p => ({ ...p, doc_type: 'external_link' }))}
                className={`flex-1 p-3 rounded-md border-2 text-left transition-all ${meta.doc_type === 'external_link' ? 'border-violet-500 bg-violet-50/50' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid="doc-type-external"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Link2 className={`w-4 h-4 ${meta.doc_type === 'external_link' ? 'text-violet-600' : 'text-slate-400'}`} />
                  <p className={`text-sm font-semibold ${meta.doc_type === 'external_link' ? 'text-violet-700' : 'text-slate-700'}`}>Linked PDF / URL</p>
                </div>
                <p className="text-[10px] text-slate-500">Paste a link to a PDF, Google Doc, or manual — it will embed inline.</p>
              </button>
            </div>

            {/* Metadata */}
            <Card className="border p-4 space-y-3">
              <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Metadata</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Title *</Label>
                  <Input value={meta.title} onChange={e => setMeta(p => ({ ...p, title: e.target.value }))} className="h-9 text-sm" data-testid="doc-title-input" />
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Category *</Label>
                  <Select value={meta.category} onValueChange={v => setMeta(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="doc-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium mb-1.5 block">Summary</Label>
                <Textarea value={meta.summary} onChange={e => setMeta(p => ({ ...p, summary: e.target.value }))} className="text-xs min-h-[60px]" />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-[11px] font-medium mb-1 block">Author</Label>
                  <Input value={meta.author} onChange={e => setMeta(p => ({ ...p, author: e.target.value }))} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[11px] font-medium mb-1 block">Version</Label>
                  <Input value={meta.version} onChange={e => setMeta(p => ({ ...p, version: e.target.value }))} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[11px] font-medium mb-1 block">Read minutes</Label>
                  <Input type="number" value={meta.readMinutes} onChange={e => setMeta(p => ({ ...p, readMinutes: parseInt(e.target.value) || 0 }))} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[11px] font-medium mb-1 block">Updated at (display)</Label>
                  <Input value={meta.updatedAt} onChange={e => setMeta(p => ({ ...p, updatedAt: e.target.value }))} placeholder="e.g. 23 Apr, 2026" className="h-8 text-xs" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] font-medium mb-1 block">Audience (comma-separated)</Label>
                  <Input value={meta.audience} onChange={e => setMeta(p => ({ ...p, audience: e.target.value }))} placeholder="Trainers, Admins" className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[11px] font-medium mb-1 block">Tags (comma-separated)</Label>
                  <Input value={meta.tags} onChange={e => setMeta(p => ({ ...p, tags: e.target.value }))} placeholder="syllabus, daily-attendance" className="h-8 text-xs" />
                </div>
              </div>
              {meta.doc_type === 'external_link' && (
                <div>
                  <Label className="text-[11px] font-medium mb-1 block flex items-center gap-1.5">
                    <Link2 className="w-3 h-3 text-violet-500" />
                    External URL *
                  </Label>
                  <Input
                    value={meta.external_url}
                    onChange={e => setMeta(p => ({ ...p, external_url: e.target.value }))}
                    placeholder="https://drive.google.com/file/d/… or direct .pdf URL"
                    className="h-8 text-xs font-mono"
                    data-testid="doc-external-url-input"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Google Drive / Docs / Sheets / Slides / Dropbox share URLs are auto-rewritten for embedding. Direct PDF URLs work out of the box.
                  </p>
                </div>
              )}
            </Card>

            {/* Sections — hidden for linked docs */}
            {meta.doc_type === 'authored' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Sections &amp; Content</p>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addSection} data-testid="add-section-btn"><Plus className="w-3 h-3 mr-1" />Add Section</Button>
              </div>

              {sections.map((s, si) => (
                <SectionEditor
                  key={si}
                  section={s}
                  index={si}
                  total={sections.length}
                  onChange={(field, val) => updateSection(si, field, val)}
                  onDelete={() => deleteSection(si)}
                  onMove={(dir) => moveSection(si, dir)}
                  onAddBlock={(type) => addBlock(si, type)}
                  onDeleteBlock={(bi) => deleteBlock(si, bi)}
                  onUpdateBlock={(bi, updater) => updateBlock(si, bi, updater)}
                />
              ))}

              {sections.length === 0 && (
                <Card className="border-dashed border-2 p-8 text-center">
                  <p className="text-xs text-slate-400">No sections yet. Click "Add Section" above.</p>
                </Card>
              )}
            </div>
            )}

            {/* Linked doc preview */}
            {meta.doc_type === 'external_link' && meta.external_url && (
              <Card className="border p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider flex items-center gap-1.5">
                    <ExternalLink className="w-3 h-3" />Embedded Preview
                  </p>
                  <a href={meta.external_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-violet-600 hover:underline">Open in new tab</a>
                </div>
                <iframe
                  key={meta.external_url}
                  src={toEmbeddableUrl(meta.external_url)}
                  className="w-full h-[380px] border border-slate-200 rounded"
                  title="Preview"
                />
              </Card>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t mt-2">
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="text-xs h-9 bg-violet-600 hover:bg-violet-700" onClick={save} disabled={saving || loading} data-testid="save-document-btn">
            {saving ? 'Saving...' : (mode === 'edit' ? 'Update Document' : 'Create Document')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SectionEditor({ section, index, total, onChange, onDelete, onMove, onAddBlock, onDeleteBlock, onUpdateBlock }) {
  const [open, setOpen] = useState(true);

  return (
    <Card className="border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50/70 border-b">
        <span className="text-[10px] font-bold text-violet-600 bg-violet-100 rounded px-1.5 py-0.5 font-mono">{String(index + 1).padStart(2, '0')}</span>
        <Input
          value={section.heading}
          onChange={e => onChange('heading', e.target.value)}
          placeholder="Section heading"
          className="h-8 text-sm font-semibold flex-1"
          data-testid={`section-heading-${index}`}
        />
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onMove(-1)} disabled={index === 0}><ChevronUp className="w-3.5 h-3.5 text-slate-400" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onMove(1)} disabled={index === total - 1}><ChevronDown className="w-3.5 h-3.5 text-slate-400" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setOpen(!open)}>{open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDelete} data-testid={`delete-section-${index}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
      </div>

      {open && (
        <div className="p-3 space-y-3">
          <Input
            value={section.subheading || ''}
            onChange={e => onChange('subheading', e.target.value)}
            placeholder="Subheading (optional)"
            className="h-8 text-xs text-slate-500"
          />

          {(section.blocks || []).map((b, bi) => (
            <BlockEditor
              key={bi}
              block={b}
              onChange={(updater) => onUpdateBlock(bi, updater)}
              onDelete={() => onDeleteBlock(bi)}
            />
          ))}

          <div className="flex items-center gap-1 flex-wrap pt-1">
            <span className="text-[10px] text-slate-400 mr-1">+ Add block:</span>
            {Object.keys(BLOCK_TEMPLATES).map(t => (
              <Button key={t} size="sm" variant="outline" className="h-6 text-[10px] px-2 capitalize" onClick={() => onAddBlock(t)} data-testid={`add-block-${t}`}>
                {t}
              </Button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}


function BlockEditor({ block, onChange, onDelete }) {
  const badgeColor = {
    paragraph: 'bg-slate-50 text-slate-600',
    steps: 'bg-violet-50 text-violet-700',
    bullets: 'bg-blue-50 text-blue-700',
    callout: 'bg-amber-50 text-amber-700',
    table: 'bg-emerald-50 text-emerald-700',
    faq: 'bg-rose-50 text-rose-700',
  }[block.type] || 'bg-slate-50 text-slate-600';

  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-2 bg-white">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className={`text-[9px] ${badgeColor} border-transparent uppercase tracking-wider font-semibold`}>{block.type}</Badge>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Duplicate" onClick={() => onChange(b => ({ ...JSON.parse(JSON.stringify(b)) }))}><Copy className="w-3 h-3 text-slate-300" /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onDelete}><Trash2 className="w-3 h-3 text-red-300" /></Button>
        </div>
      </div>

      {block.type === 'paragraph' && (
        <Textarea value={block.text || ''} onChange={e => onChange(b => ({ ...b, text: e.target.value }))} className="text-xs min-h-[60px]" placeholder="Paragraph text..." />
      )}

      {block.type === 'bullets' && (
        <ArrayStringEditor items={block.items || []} placeholder="Bullet text..." onChange={items => onChange(b => ({ ...b, items }))} />
      )}

      {block.type === 'steps' && (
        <div className="space-y-2">
          {(block.items || []).map((step, i) => (
            <div key={i} className="border rounded p-2 space-y-1.5 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <span className="text-[9px] bg-slate-900 text-white rounded w-4 h-4 flex items-center justify-center font-bold">{i + 1}</span>
                <Input value={step.title || ''} onChange={e => onChange(b => ({ ...b, items: b.items.map((x, j) => j === i ? { ...x, title: e.target.value } : x) }))} placeholder="Step title" className="h-7 text-xs flex-1 font-medium" />
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onChange(b => ({ ...b, items: b.items.filter((_, j) => j !== i) }))}><Trash2 className="w-3 h-3 text-red-300" /></Button>
              </div>
              <Textarea value={step.detail || ''} onChange={e => onChange(b => ({ ...b, items: b.items.map((x, j) => j === i ? { ...x, detail: e.target.value } : x) }))} placeholder="Detail" className="text-[11px] min-h-[40px]" />
              <Input value={step.hint || ''} onChange={e => onChange(b => ({ ...b, items: b.items.map((x, j) => j === i ? { ...x, hint: e.target.value } : x) }))} placeholder="Hint (optional)" className="h-7 text-[11px] text-violet-600" />
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => onChange(b => ({ ...b, items: [...(b.items || []), { title: 'New step', detail: '' }] }))}><Plus className="w-2.5 h-2.5 mr-1" />Add Step</Button>
        </div>
      )}

      {block.type === 'callout' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-[10px] text-slate-500">Tone:</Label>
            <Select value={block.tone || 'info'} onValueChange={v => onChange(b => ({ ...b, tone: v }))}>
              <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info" className="text-xs">Info (blue)</SelectItem>
                <SelectItem value="success" className="text-xs">Success (green)</SelectItem>
                <SelectItem value="warning" className="text-xs">Warning (amber)</SelectItem>
                <SelectItem value="tip" className="text-xs">Tip (violet)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input value={block.title || ''} onChange={e => onChange(b => ({ ...b, title: e.target.value }))} placeholder="Title (optional)" className="h-7 text-xs font-medium" />
          <Textarea value={block.text || ''} onChange={e => onChange(b => ({ ...b, text: e.target.value }))} placeholder="Callout body" className="text-xs min-h-[50px]" />
        </div>
      )}

      {block.type === 'table' && (
        <TableBlockEditor block={block} onChange={onChange} />
      )}

      {block.type === 'faq' && (
        <div className="space-y-2">
          {(block.items || []).map((q, i) => (
            <div key={i} className="border rounded p-2 space-y-1.5 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-slate-400 font-mono">Q{i + 1}</span>
                <Input value={q.question || ''} onChange={e => onChange(b => ({ ...b, items: b.items.map((x, j) => j === i ? { ...x, question: e.target.value } : x) }))} placeholder="Question" className="h-7 text-xs flex-1" />
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onChange(b => ({ ...b, items: b.items.filter((_, j) => j !== i) }))}><Trash2 className="w-3 h-3 text-red-300" /></Button>
              </div>
              <Textarea value={q.answer || ''} onChange={e => onChange(b => ({ ...b, items: b.items.map((x, j) => j === i ? { ...x, answer: e.target.value } : x) }))} placeholder="Answer" className="text-[11px] min-h-[40px]" />
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => onChange(b => ({ ...b, items: [...(b.items || []), { question: '', answer: '' }] }))}><Plus className="w-2.5 h-2.5 mr-1" />Add Q&amp;A</Button>
        </div>
      )}
    </div>
  );
}


function ArrayStringEditor({ items, placeholder, onChange }) {
  return (
    <div className="space-y-1.5">
      {items.map((t, i) => (
        <div key={i} className="flex gap-2">
          <span className="text-[10px] text-slate-400 pt-1.5 font-mono">{i + 1}.</span>
          <Input value={t} onChange={e => onChange(items.map((x, j) => j === i ? e.target.value : x))} placeholder={placeholder} className="h-7 text-xs flex-1" />
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3 text-red-300" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => onChange([...items, ''])}><Plus className="w-2.5 h-2.5 mr-1" />Add</Button>
    </div>
  );
}


function TableBlockEditor({ block, onChange }) {
  const headers = block.headers || [];
  const rows = block.rows || [];

  const setHeader = (i, val) => onChange(b => ({ ...b, headers: b.headers.map((h, j) => j === i ? val : h) }));
  const addColumn = () => onChange(b => ({ ...b, headers: [...b.headers, 'New column'], rows: b.rows.map(r => [...r, '']) }));
  const removeColumn = (i) => onChange(b => ({ ...b, headers: b.headers.filter((_, j) => j !== i), rows: b.rows.map(r => r.filter((_, j) => j !== i)) }));
  const setCell = (ri, ci, val) => onChange(b => ({ ...b, rows: b.rows.map((r, i) => i === ri ? r.map((c, j) => j === ci ? val : c) : r) }));
  const addRow = () => onChange(b => ({ ...b, rows: [...b.rows, headers.map(() => '')] }));
  const removeRow = (i) => onChange(b => ({ ...b, rows: b.rows.filter((_, j) => j !== i) }));

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto border rounded">
        <table className="text-xs min-w-full">
          <thead className="bg-slate-50">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="p-1 border-r last:border-r-0">
                  <div className="flex items-center gap-1">
                    <Input value={h} onChange={e => setHeader(i, e.target.value)} className="h-7 text-[11px] font-semibold" />
                    <button onClick={() => removeColumn(i)} className="p-0.5"><Trash2 className="w-2.5 h-2.5 text-red-300" /></button>
                  </div>
                </th>
              ))}
              <th className="p-1 w-8"><Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={addColumn}><Plus className="w-3 h-3" /></Button></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-t">
                {r.map((c, ci) => (
                  <td key={ci} className="p-1 border-r last:border-r-0">
                    <Input value={c} onChange={e => setCell(ri, ci, e.target.value)} className="h-7 text-[11px]" />
                  </td>
                ))}
                <td className="p-1">
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeRow(ri)}><Trash2 className="w-3 h-3 text-red-300" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={addRow}><Plus className="w-2.5 h-2.5 mr-1" />Add Row</Button>
    </div>
  );
}


// ─── Delete confirm dialog ────────────────────────────────────

export function DeleteDocumentDialog({ open, doc, onCancel, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={() => onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-500" />Delete this document?</AlertDialogTitle>
          <AlertDialogDescription>
            "<span className="font-semibold text-slate-700">{doc?.title}</span>" will be permanently removed. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-xs" onClick={onConfirm}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
